import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FiShield, FiUser, FiKey, FiArrowLeft } from "react-icons/fi";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      if (!user?.is_staff && !user?.is_superuser) {
        logout();
        setError("This account does not have admin access.");
        return;
      }
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err.message || "Admin login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Dark Matrix-like Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,122,0,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-30" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-[#141417] border border-white/5 rounded-[32px] p-8 md:p-10 shadow-2xl backdrop-blur-xl">
          <div className="text-center mb-10">
            <img src="/logo.png" alt="Chafadi" className="h-8 mx-auto mb-8 invert opacity-80" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
              <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest">Admin Authorization</span>
            </div>
            <h1 className="text-2xl font-black text-white mb-2 leading-tight">Control Panel</h1>
            <p className="text-gray-400 text-sm font-medium">Please authenticate to access the admin services.</p>
          </div>

          {error && (
            <div className="bg-rose-500/10 text-rose-400 p-4 rounded-2xl border border-rose-500/20 mb-8 text-xs font-black uppercase tracking-widest flex items-center gap-3">
              <FiShield className="text-lg" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 group-focus-within:text-orange-500 transition-colors">
                  <FiUser />
                </div>
                <input
                  type="email"
                  placeholder="Admin Identifier"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-14 bg-[#1c1c21] border border-white/5 rounded-2xl pl-12 pr-4 text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all font-semibold"
                  required
                />
              </div>

              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 group-focus-within:text-orange-500 transition-colors">
                  <FiKey />
                </div>
                <input
                  type="password"
                  placeholder="Secret Key"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-14 bg-[#1c1c21] border border-white/5 rounded-2xl pl-12 pr-4 text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all font-semibold"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-orange-500 text-white font-black rounded-2xl shadow-xl shadow-orange-500/20 hover:bg-orange-600 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 uppercase tracking-widest text-xs pt-1"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Authorize Access"
              )}
            </button>

            <div className="text-center pt-4">
              <Link to="/login" className="text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-white transition-colors flex items-center justify-center gap-2">
                <FiArrowLeft />
                Return to Marketplace
              </Link>
            </div>
          </form>
        </div>
        
        <p className="text-center mt-8 text-[10px] text-gray-600 font-bold uppercase tracking-[0.2em]">
          Secure Infrastructure &copy; Chafadi {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
